import {updateIsShowInAppBrowser} from '@redux/shared/shared'
import {isFacebookInAppBrowser} from '@utils'
import {useRef, useState, useEffect} from 'react'
import {useDispatch} from 'react-redux'
import RecordRTC from 'recordrtc'
import {datadogRum} from '@datadog/browser-rum'

type UseRecordReturn = [boolean, Blob | null, () => Promise<void>, () => void]

export const useRecord = (): UseRecordReturn => {
  const recorderRef = useRef<RecordRTC | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [isRecording, setIsRecording] = useState(false)
  const [recordingBlob, setRecordingBlob] = useState<Blob | null>(null)
  const dispatch = useDispatch()

  useEffect(() => {
    // Cleanup function
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
      }
    }
  }, [])

  const startRecording = async () => {
    const url = window.location.href
    const isAndroid = /android/i.test(navigator.userAgent)
    if (isAndroid && isFacebookInAppBrowser()) {
      datadogRum.addAction('record_android_fb_browser', {
        isAndroid: true,
        isFacebookInAppBrowser: true,
        url,
      })
      alert('Trình duyệt của bạn không hỗ trợ ghi âm')
      dispatch(updateIsShowInAppBrowser(true))
      return
    }

    if (!navigator.mediaDevices) {
      datadogRum.addAction('not_navigator_media_devices', {
        isFacebookInAppBrowser: isFacebookInAppBrowser(),
        url,
      })
      alert('Trình duyệt của bạn không hỗ trợ ghi âm')
      if (isFacebookInAppBrowser()) {
        dispatch(updateIsShowInAppBrowser(true))
        return
      }
      return
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({audio: true})
      streamRef.current = stream

      recorderRef.current = new RecordRTC(stream, {
        type: 'audio',
        mimeType: 'audio/wav',
        recorderType: RecordRTC.StereoAudioRecorder,
        numberOfAudioChannels: 1,
        desiredSampRate: 16000,
        bufferSize: 4096,
      })

      recorderRef.current.startRecording()
      setIsRecording(true)
    } catch (error) {
      datadogRum.addAction('start_recording_error', {
        url,
        error,
      })
      console.error('Lỗi truy cập microphone:', error)
      alert('Lỗi truy cập microphone')
    }
  }

  const stopRecording = () => {
    if (recorderRef.current) {
      recorderRef.current.stopRecording(() => {
        const blob = recorderRef.current?.getBlob() || null
        setRecordingBlob(blob)

        // Dọn dẹp
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop())
        }
        setIsRecording(false)
      })
    }
  }

  return [isRecording, recordingBlob, startRecording, stopRecording]
}
